import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  RouterStateSnapshot,
  UrlTree,
  Router,
} from '@angular/router';

import { EMPTY, empty, Observable, of } from 'rxjs';
import { HandleTokenService } from '../services/handle-token.service';
import { map, catchError } from 'rxjs/operators';
import { IExtraData, IPutRecord, ITransactions } from '../models/app.model';
import { AppFacade } from '../../facades/app.facade';
import { APP_CONSTANTS as CONST } from '@app/app.constants';
import { APP_ROUTES as ROUTES } from '../../app.routes';
import { TRANSACTIONS_MOCK as TRANSACTIONS } from '../../data/mocks/data/transactions';

import { environment } from '@environment';
import { OffersFacade } from '../../facades/offers.facade';
import { EVENTS } from '@app/data/events/events-by-category';
import { StorageService } from '@app/data/browser/storage.service';

@Injectable({
  providedIn: 'root',
})
export class TransactionGuard implements CanActivate {
  private _isPutRecordSent: boolean = false;

  constructor(
    private _route: Router,
    private _handleTokenService: HandleTokenService,
    private _storageService: StorageService,
    private _appFacade: AppFacade,
    private _offersAFacade: OffersFacade
  ) {}

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this._handleTransactionToken(route);
  }

  private _handleTransactionToken(
    route: ActivatedRouteSnapshot
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    const token: string =
      route.queryParams[CONST.PROP_URL_TRASACTION_ID] ??
      this._handleTokenService.getTransactionId();
    if (!!token) this._handleTokenService.setTransactionId(token);

    return this._checkIsTestEnv()
      ? this._handleMockTransaction().pipe(map((_) => true))
      : this._handleTransaction(token);
  }

  private _handleTransaction(token: string) {
    return !!token
      ? this._appFacade.getTransactions(token).pipe(
          map((transaction: ITransactions) => {
            this._storageService.removeItem('purchaseLessOnboarding');
            const { amount } = transaction;
            this._offersAFacade.setOfferAmount(amount);
            this._handleTokenService.setTransactionToken(token);
            this._appFacade.setTransactions(transaction);
            if (!this._isPutRecordSent) {
              const data: IPutRecord = EVENTS.REDIRECT_RESPONSE;
              const extraData: IExtraData = {
                response: transaction,
              };
              this._appFacade.sendPutRecord(data, extraData);
              this._isPutRecordSent = true;
            }
            this._appFacade.setIsLoading();
            return true;
          }),
          catchError((_) => of(this._closeSession()))
        )
      : this._continueWithPurchaselessOnboarding();
  }

  private _handleMockTransaction(): Observable<void | boolean | UrlTree> {
    const sessionId: string = this._storageService.getItem('sessionId');
    const purchaseLessOnboardingFlag: boolean =
      this._storageService.getItem('purchaseLessOnboarding');

    if (sessionId && purchaseLessOnboardingFlag) {
      return of(true);
    }

    const transaction = TRANSACTIONS.PENDING;
    const { amount, token } = transaction;
    this._offersAFacade.setOfferAmount(amount);
    this._handleTokenService.setTransactionToken(token);
    return !!token ? of(this._appFacade.setTransactions(transaction)) : of(this._closeSession());
  }

  private _closeSession(): UrlTree {
    const route: string = ROUTES.ERROR;
    this._appFacade.closeSession();
    return this._route.createUrlTree([route]);
  }

  private _checkIsTestEnv(): boolean {
    const { production, isTestServer } = environment;
    return !production && isTestServer;
  }

  private _continueWithPurchaselessOnboarding(): boolean | UrlTree {
    const sessionId: string = this._storageService.getItem('sessionId');
    const purchaseLessOnboardingFlag: boolean =
      this._storageService.getItem('purchaseLessOnboarding');

    return !!sessionId && !!purchaseLessOnboardingFlag ? true : this._closeSession();
  }
}
